import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { AgGridReact } from 'ag-grid-react';
import {
  CellClickedEvent,
  FilterModel,
  GridReadyEvent,
  IServerSideDatasource,
  IServerSideGetRowsParams,
} from 'ag-grid-community';
import Datetime from 'react-datetime';
import moment from 'moment';
import { hideModal, showModal } from 'erisxkit/client';
import { useAgGridCommon } from '../../../common/table/agGrid/AgGridCommon';
import tradeLogGridColDefs from './TradeLogGridColDefs';
import {
  commonPaginationOptions,
  externalFilterOnlyOpts,
  mergeGridOpts,
} from '../../../utils/agGrid/options';
import { CONFIRM_ACTION_WITH_PAYLOAD } from '../../../constants/modalTypes';
import { Form, List, Dropdown, Button, Input } from 'semantic-ui-react';
import { useAppDispatch } from '../../../hooks/useAppDispatch';
import { fetchTradesPromise } from '../../../reducers/tradesReducer';
import { createReversal } from '../../../actions/reconciliationActions';
import { ServerSideQuery } from '../../../utils/agGrid/types';
import TradeLog from '../../../ts/models/TradeLog.model';
import { Wrapper } from './TradeLogGrid.styles';
import styled from 'styled-components';
import { useAppSelector } from '../../../hooks/useAppSelector';
import {
  getMemoizedContractsAsOptions,
  contractSymbols,
} from '../../../reducers/contractsReducer';
import { getFuturesSpotProductSymbolsAsOptions } from '../../../reducers/futuresProductReducer';
import parseAGRequest from 'utils/agGrid/ssrm/parseAGRequest';
import useExternalFilter from 'utils/agGrid/hooks/useExternalFilter';
import AccountSelectionContainer from 'common/containers/AccountSelectionContainer';
import { isEmpty } from 'lodash';
import { processSSRM } from 'utils/agGrid/ssrm/processSSRM';
import { TRADES_SCREEN_READ_ONLY_ROLES } from 'containers/TradeLogContainer';
import { getUserHasAnyRole } from 'reducers/usersReducer';

const gridOptions = mergeGridOpts(commonPaginationOptions, {
  rowStyle: { cursor: 'pointer' },
  ...externalFilterOnlyOpts,
});

const StyledSegment = styled.div`
  background-color: #e0e0e0;
  padding: 1em;
  border-radius: 4px;
  font-family: Arial, sans-serif;
  line-height: 1.5;
  color: rgba(0, 0, 0, 0.6);
`;

const Fields = styled.div`
  display: flex;
  gap: 0.6rem;
  flex-wrap: wrap;
`;

export const TRADE_LOG_GRID_NAME = 'trade-log';

export const DetailCellRenderer = ({ data }: { data?: TradeLog }) => (
  <StyledSegment>
    <List.Content>
      <List.Item content={`UUID: ${data?.uuid}`} />
      <List.Item content={`Description: ${data?.description}`} />
      <List.Item content={`Created By: ${data?.createdBy}`} />
      <List.Item content={`Account ID: ${data?.accountId}`} />
      <List.Item
        content={`Dco Account ID - Name: ${data?.dcoAccountId} - ${data?.dcoAccountName}`}
      />
      <List.Item
        content={`Clearing Fee Account ID - Name: ${data?.clearingFeeAccountId} - ${data?.clearingFeeAccountName}`}
      />
      <List.Item
        content={`Exchange Fee Account ID - Name: ${data?.exchangeFeeAccountId} - ${data?.exchangeFeeAccountName}`}
      />
    </List.Content>
  </StyledSegment>
);

const getChangedFilters = (
  previousFilters: FilterModel,
  currentFilters: FilterModel,
) => {
  const changedFilters: FilterModel = {};
  for (const key in currentFilters) {
    if (
      JSON.stringify(currentFilters[key]) !==
      JSON.stringify(previousFilters[key])
    ) {
      changedFilters[key] = currentFilters[key];
    }
  }
  return changedFilters;
};

const TradeLogGrid = () => {
  const [lastFilters, setLastFilters] = useState({});
  const detailCellRenderer = useCallback(DetailCellRenderer, []);
  const gridRef = useRef<AgGridReact>(null);
  const dispatch = useAppDispatch();
  const isReadOnlyRole = useAppSelector((state) =>
    getUserHasAnyRole(state, TRADES_SCREEN_READ_ONLY_ROLES),
  );
  const contracts = useAppSelector(getMemoizedContractsAsOptions);
  const productOptions = useAppSelector(getFuturesSpotProductSymbolsAsOptions);

  const [externalFilters, setExternalFilters] = useState({});
  const context = useMemo(() => externalFilters, [externalFilters]);

  const { gridVisible, initialGridSettings, onStateUpdated } = useAgGridCommon({
    gridName: TRADE_LOG_GRID_NAME,
  });

  const fetchData = useCallback(
    async (params: IServerSideGetRowsParams) => {
      const { request, context } = params;
      const body = parseAGRequest(request, { transformAttrs: true });
      // Base filters from the grid
      const filters = body?.filter || [];
      const matchColDefs = ['contract_symbol', 'product_symbol'];
      const parsedFilters = filters.map((filter) =>
        matchColDefs.includes(filter.attr)
          ? { ...filter, op: 'match' }
          : filter,
      );

      if (context?.dateFrom) {
        parsedFilters.push({
          attr: 'date',
          op: 'gte',
          value: context?.dateFrom,
        });
      }
      if (context?.dateTo) {
        parsedFilters.push({
          attr: 'date',
          op: 'lte',
          value: context?.dateTo,
        });
      }

      const reqBody: ServerSideQuery = {
        ...body,
        filter: [...(parsedFilters ? parsedFilters : [])],
      };
      const response = await fetchTradesPromise(reqBody, dispatch);
      return response?.tradeJournals;
    },
    [dispatch],
  );

  const getRows = useCallback(
    (params: IServerSideGetRowsParams) =>
      processSSRM<TradeLog>(params, fetchData),
    [fetchData],
  );

  const onGridReadyCallback = useCallback(
    (params: GridReadyEvent) => {
      const dataSource: IServerSideDatasource = {
        getRows,
      };
      params.api?.setGridOption('serverSideDatasource', dataSource);
    },
    [getRows],
  );

  const showReversalModal = useCallback(
    (row: TradeLog) => {
      dispatch(
        showModal(CONFIRM_ACTION_WITH_PAYLOAD, {
          closeOnDimmerClick: true,
          closeOnEscape: true,
          payload: row,
          header: 'Confirm Trade Reversal',
          hideModal: () => dispatch(hideModal(CONFIRM_ACTION_WITH_PAYLOAD)), // Pasamos la prop si es necesario
          onConfirm: () => {
            dispatch(createReversal({ uuid: row.uuid, showToast: true }));
            dispatch(hideModal(CONFIRM_ACTION_WITH_PAYLOAD));
          },
          msg: (
            <p>
              Reverse trade with ID <span className="mono">{row?.uuid}</span>?
            </p>
          ),
        }),
      );
    },
    [dispatch],
  );

  const onCellClicked = useCallback(
    (event: CellClickedEvent) => {
      const { data, colDef } = event;
      if (data) {
        const columnField = colDef.field;
        if (columnField === 'reversal' && !isReadOnlyRole) {
          showReversalModal(data);
        }
      }
    },
    [isReadOnlyRole, showReversalModal],
  );

  useEffect(() => {
    if (!initialGridSettings?.columnSizing) {
      gridRef.current?.api?.sizeColumnsToFit();
    }
  }, [initialGridSettings?.columnSizing]);

  const columnDefs = useMemo(
    () => tradeLogGridColDefs({ isReadOnlyRole }),
    [isReadOnlyRole],
  );

  const onFilterChanged = useCallback(() => {
    const gridApi = gridRef.current?.api;
    const currentFilters = gridApi?.getFilterModel();

    const hasProductChanged =
      currentFilters &&
      'productSymbol' in getChangedFilters(lastFilters, currentFilters);

    if (currentFilters) {
      //Call the Contract endpoint just if the Product filter changed.
      if (hasProductChanged) {
        const { filter: productSymbolFilter } =
          currentFilters.productSymbol || {};
        const filter = [
          {
            attr: 'product_symbol',
            op: 'match',
            value: productSymbolFilter,
          },
        ];
        // Dispatch the related contracts for the selected product
        dispatch(contractSymbols({ filter }));
      }
      setLastFilters(currentFilters);
    }
  }, [dispatch, lastFilters]);

  const onDateChange = useCallback(
    (value: Record<string, string>) => {
      setExternalFilters({ ...externalFilters, ...value });
    },
    [externalFilters],
  );

  const clearDateFilters = useCallback(() => {
    setExternalFilters({});
  }, []);

  const {
    setExternalFilter,
    getFilterValue,
    setFilterFromEvent,
    clearFilters,
    hasFiltersApplied,
    onGridReady,
  } = useExternalFilter({ gridRef, onGridReadyCallback });

  const onClear = useCallback(() => {
    clearFilters();
    clearDateFilters();
  }, [clearDateFilters, clearFilters]);

  const isInitialRender = useRef(true);
  useEffect(() => {
    if (isInitialRender.current) {
      isInitialRender.current = false;
      return;
    }
    if (gridRef.current && gridRef.current.api) {
      gridRef.current.api.refreshServerSide({ purge: true });
    }
  }, [externalFilters]);

  const clearDisabled = useMemo(() => {
    const hasExternalFilters = !isEmpty(externalFilters);
    return !hasFiltersApplied && !hasExternalFilters;
  }, [externalFilters, hasFiltersApplied]);

  return (
    <>
      <Fields className="ui form">
        <Form.Field
          control={Datetime}
          className="ui input datetime"
          label="Begin Date"
          name="beginTime"
          id="beginTime"
          defaultValue=""
          dateFormat="YYYY-MM-DD"
          timeFormat={false}
          value={context?.dateFrom}
          inputProps={{ placeholder: 'Filter Begin Time' }}
          onChange={(currentDate) => {
            const formattedDate = moment(currentDate).format('YYYY-MM-DD');
            onDateChange({ dateFrom: formattedDate });
          }}
          closeOnSelect
        />
        <Form.Field
          control={Datetime}
          className="ui input datetime"
          label="End Date"
          name="endTime"
          id="endTime"
          defaultValue=""
          dateFormat="YYYY-MM-DD"
          timeFormat={false}
          value={context?.dateTo}
          inputProps={{ placeholder: 'Filter End Time' }}
          onChange={(currentDate) => {
            const formattedDate = moment(currentDate).format('YYYY-MM-DD');
            onDateChange({ dateTo: formattedDate });
          }}
          closeOnSelect
        />
        <div className="field">
          <label>Product</label>
          <Dropdown
            value={getFilterValue('productSymbol')}
            placeholder="Click to select Product"
            onChange={(_e, { name: colId, value }) => {
              if (value) {
                setExternalFilter({ colId, value, op: 'equals' });
              }
            }}
            name="productSymbol"
            selection
            labeled
            options={productOptions}
          />
        </div>
        <div className="field">
          <label>Contract</label>
          <Dropdown
            value={getFilterValue('contractSymbol')}
            placeholder={
              gridRef.current?.api?.getFilterModel()?.productSymbol
                ? 'Select Contract'
                : 'Select a product to see contract list'
            }
            onChange={(_e, { name: colId, value }) => {
              //If clearable is enabled, we need to send empty string to the filter in order to deselect the Contract.
              setExternalFilter({ colId, value: value || '', op: 'equals' });
            }}
            name="contractSymbol"
            clearable
            labeled
            options={contracts}
            search
            selection
            noResultsMessage="No results found."
          />
        </div>
        <div className="field">
          <label>Price</label>
          <Input
            name="px"
            type="string"
            value={getFilterValue('px')}
            onChange={(e) => {
              setFilterFromEvent(e, { op: 'equals' });
            }}
          />
        </div>
        <div className="field">
          <label>Account</label>
          <AccountSelectionContainer
            label=""
            placeholder="Type an Account"
            name="accountId"
            onChange={(e, { value }) => {
              setExternalFilter({ colId: 'accountId', value, op: 'equals' });
            }}
            value={getFilterValue('accountId')}
          />
        </div>
        <div className="field">
          <label>Trade Id</label>
          <Input
            name="tradeId"
            type="string"
            value={getFilterValue('tradeId')}
            onChange={(e) => {
              setFilterFromEvent(e, { op: 'equals' });
            }}
          />
        </div>
        <div className="field">
          <label>Spread Id</label>
          <Input
            name="secondaryExecId"
            type="string"
            value={getFilterValue('secondaryExecId')}
            onChange={(e) => {
              setFilterFromEvent(e, { op: 'equals' });
            }}
          />
        </div>
        <div className="field">
          <label>Quantity</label>
          <Input
            name="qty"
            type="string"
            value={getFilterValue('qty')}
            onChange={(e) => {
              setFilterFromEvent(e, { op: 'equals' });
            }}
          />
        </div>
        <Button
          color="red"
          disabled={clearDisabled}
          id="clear-filters"
          content="Clear"
          onClick={onClear}
          style={{ height: '38px', marginTop: '24px' }}
        />
      </Fields>
      {gridVisible ? (
        <Wrapper>
          <div className="ag-theme-quartz">
            <AgGridReact
              ref={gridRef}
              columnDefs={columnDefs}
              masterDetail={true}
              context={context}
              gridOptions={gridOptions}
              onStateUpdated={onStateUpdated}
              initialState={initialGridSettings}
              rowModelType="serverSide"
              onGridReady={onGridReady}
              onCellClicked={onCellClicked}
              detailCellRenderer={detailCellRenderer}
              detailRowAutoHeight={true}
              onFilterChanged={onFilterChanged}
              blockLoadDebounceMillis={550}
            />
          </div>
        </Wrapper>
      ) : null}
    </>
  );
};

export default TradeLogGrid;
